import { get } from 'svelte/store';

import type { Device } from 'src/types/Device';
import type { PVSetupTypes } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';

import { devices } from 'src/stores/lists/devices';
import { home_data } from 'src/stores/account/user';

import { requestEditDevice } from 'src/components/Lists/list-helpers';
import { delete_home, save_home } from 'src/helpers/home_helpers';
import { APPLIANCE_TYPES_VALUES } from 'src/constants';
import { devices_config } from '@devices/index';

import { MAIN_ENERGY_METER_KEY } from './main-energy-meter-helpers';
import {
	GENERATED_ENERGY_APPLIANCE_TYPE,
	GENERATED_ENERGY_USAGE,
	GRID_SOURCE_APPLIANCE_TYPE,
	GRID_SOURCE_USAGE,
	getConfiguredMeterIdsByRole,
	isEligibleSolarMeterDevice,
	planSolarSetupApplianceTypes,
	removeDeviceFromPvConfigurations,
	type ApplianceSyncUsage,
	type ApplianceTypeSyncChange,
	type PvConfigurations,
} from './solar-setup-helpers';

const applianceTypeForUsage = (usage: ApplianceSyncUsage): number => {
	if (usage === GRID_SOURCE_USAGE) return GRID_SOURCE_APPLIANCE_TYPE;
	if (usage === GENERATED_ENERGY_USAGE) return GENERATED_ENERGY_APPLIANCE_TYPE;
	return APPLIANCE_TYPES_VALUES.other;
};

export const applyApplianceTypeUsage = async (
	device: Device,
	usage: ApplianceSyncUsage,
): Promise<boolean> => {
	if (device.relay_usage === usage && device.appliance_type === applianceTypeForUsage(usage)) {
		return true;
	}

	const res = await requestEditDevice({
		...device,
		appliance_type: applianceTypeForUsage(usage),
		relay_usage: usage,
	});
	return Boolean(res?.isok);
};

export const applyApplianceTypeSyncChanges = async (
	changes: ApplianceTypeSyncChange[],
	deviceMap: Record<string, Device> = get(devices) as Record<string, Device>,
): Promise<boolean> => {
	const results = await Promise.all(
		changes.map((change) => {
			const device = deviceMap[change.deviceId];
			if (!device) return true;
			return applyApplianceTypeUsage(device, change.usage);
		}),
	);
	return results.every(Boolean);
};

export const removeDeviceFromHomeEnergyDesignations = async (deviceId: string): Promise<boolean> => {
	const homeData = get(home_data);
	const mainEnergyMeterId = (homeData?.[MAIN_ENERGY_METER_KEY] as string) || null;

	if (mainEnergyMeterId === deviceId) {
		const deleted = await delete_home(MAIN_ENERGY_METER_KEY);
		if (!deleted?.isok) return false;
	}

	const configurations = (homeData?.pv_configurations || {}) as PvConfigurations;
	const nextConfigurations = removeDeviceFromPvConfigurations(configurations, deviceId);
	const hadConfig = Object.keys(configurations).length > 0;
	const hasConfig = Object.keys(nextConfigurations).length > 0;

	if (!hadConfig) return true;

	if (!hasConfig) {
		const deleted = await delete_home('pv_configurations');
		return Boolean(deleted?.isok);
	}

	const saved = await save_home('pv_configurations', nextConfigurations);
	return Boolean(saved?.isok);
};

export const clearSolarSetupFromHome = async (): Promise<boolean> => {
	const homeData = get(home_data);
	const configurations = (homeData?.pv_configurations || {}) as PvConfigurations;
	const previousGridIds = getConfiguredMeterIdsByRole(configurations, 'grid');
	const previousSolarIds = getConfiguredMeterIdsByRole(configurations, 'solar');
	const mainEnergyMeterId = (homeData?.[MAIN_ENERGY_METER_KEY] as string) || null;

	if (Object.keys(configurations).length > 0) {
		const deleted = await delete_home('pv_configurations');
		if (!deleted?.isok) return false;
	}

	if (homeData?.net_metering_type) {
		await delete_home('net_metering_type');
	}

	await applyApplianceTypeSyncChanges(
		planSolarSetupApplianceTypes({
			previousGridIds,
			previousSolarIds,
			nextGridIds: [],
			nextSolarIds: [],
			mainEnergyMeterId,
		}),
	);

	return true;
};

export const getSuggestedSolarGridMeterId = (
	homeData: { [MAIN_ENERGY_METER_KEY]?: string } | null,
	setupType: PVSetupTypes,
	deviceMap: Record<string, Device>,
): string | null => {
	const mainId = (homeData?.[MAIN_ENERGY_METER_KEY] as string) || null;
	if (!mainId) return null;
	const device = deviceMap[mainId];
	if (!device) return null;
	return isEligibleSolarMeterDevice(
		device,
		setupType,
		'grid',
		Boolean(devices_config[device.type]?.isPM),
	)
		? mainId
		: null;
};
